import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { userAPI } from '../utils/api';

const SCOPE_GROUPS = [
  {
    label: '🏪 ผู้ใช้สาขา (Branch User)',
    description: 'สิทธิ์สำหรับสาขาร้านค้าทั่วไป',
    variant: 'success',
    scopes: [
      { value: 'orders:create', label: 'สร้าง/แก้ไขคำสั่งซื้อของตนเอง' },
      { value: 'qsc:view', label: 'ดูรายงาน QSC ของตนเอง / ส่งการปรับปรุง' },
    ],
  },
  {
    label: '🏢 HQ / คลังสินค้า',
    description: 'สิทธิ์สำหรับเจ้าหน้าที่ส่วนกลาง',
    variant: 'secondary',
    scopes: [
      { value: 'orders:manage', label: 'จัดการคำสั่งซื้อทั้งหมด (อนุมัติ/ปฏิเสธ/ลบ)' },
      { value: 'stock:intake', label: 'รับสต็อกเข้า' },
      { value: 'stock:outtake', label: 'เบิกสต็อกออก' },
      { value: 'stock:monitoring', label: 'ตรวจสอบสต็อก / ปรับปรุง / ลบรายการ' },
      { value: 'fulfillment', label: 'แดชบอร์ดเบิกสินค้า' },
      { value: 'materials:manage', label: 'เพิ่ม/แก้ไข/ลบ/นำเข้าวัตถุดิบ' },
      { value: 'qsc:manage', label: 'สร้าง/เสร็จสิ้น/ลบแบบประเมิน QSC / จัดการ Templates' },
      { value: 'qsc:improvements', label: 'รีวิวการปรับปรุง QSC (อนุมัติ/ปฏิเสธ)' },
      { value: 'recipes', label: 'จัดการสูตรต้นทุนทั้งหมด' },
      { value: 'shipping', label: 'จัดการวันจัดส่ง' },
      { value: 'suppliers', label: 'จัดการซัพพลายเออร์' },
      { value: 'buyers', label: 'จัดการผู้ซื้อ' },
      { value: 'vat', label: 'อัพเดทอัตรา VAT' },
      { value: 'reports:material', label: 'รายงานการใช้วัตถุดิบ' },
      { value: 'reports:hq', label: 'รายงาน HQ' },
    ],
  },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    fullName: '',
    isSuperuser: false,
    scopes: [],
  });
  const [newPassword, setNewPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadUsers();
    }
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await userAPI.getAll();
      setUsers(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setFormData({ username: '', password: '', email: '', fullName: '', isSuperuser: false, scopes: [] });
    setShowUserModal(true);
  };

  const handleEditClick = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      email: user.email || '',
      fullName: user.fullName || '',
      isSuperuser: !!user.isSuperuser,
      scopes: Array.isArray(user.scopes) ? user.scopes : [],
    });
    setShowUserModal(true);
  };

  const handlePasswordClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const toggleScope = (scope) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        fullName: formData.fullName,
        userType: formData.isSuperuser ? 'admin' : 'staff',
        isStaff: formData.isSuperuser,
        isSuperuser: formData.isSuperuser,
        scopes: formData.isSuperuser ? [] : formData.scopes,
      };

      if (modalMode === 'create') {
        userData.password = formData.password;
        await userAPI.create(userData);
        setSuccess('สร้างผู้ใช้สำเร็จ');
      } else {
        await userAPI.update(selectedUser.id, userData);
        setSuccess('อัปเดตข้อมูลผู้ใช้สำเร็จ');
      }
      setShowUserModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setProcessing(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      await userAPI.changePassword(selectedUser.id, { password: newPassword });
      setSuccess('เปลี่ยนรหัสผ่านสำเร็จ');
      setShowPasswordModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('ต้องการลบผู้ใช้นี้หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้')) return;
    try {
      await userAPI.delete(userId);
      setSuccess('ลบผู้ใช้สำเร็จ');
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบผู้ใช้');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await userAPI.toggleActive(userId);
      setSuccess(res.data.message);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const getUserTypeBadge = (user) => {
    if (user.isSuperuser) return <Badge bg="danger">Superadmin</Badge>;
    if (user.isStaff || user.isAdmin) return <Badge bg="warning">Admin</Badge>;
    return <Badge bg="info">Staff</Badge>;
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-people me-2"></i>
            จัดการผู้ใช้
          </h2>
          <p className="text-muted">จัดการบัญชีผู้ใช้และสิทธิ์การเข้าถึงระบบ</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreateClick}>
            <i className="bi bi-plus-circle me-2"></i>
            สร้างผู้ใช้ใหม่
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Card>
        <Card.Header className="bg-primary bg-opacity-10">
          <h5 className="mb-0">
            <i className="bi bi-list me-2"></i>
            รายการผู้ใช้ ({users.length} บัญชี)
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#999' }}></i>
              <p className="text-muted mt-3">ไม่มีผู้ใช้</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>ชื่อผู้ใช้</th>
                  <th>ชื่อ-นามสกุล</th>
                  <th>อีเมล</th>
                  <th>บทบาท</th>
                  <th>สิทธิ์</th>
                  <th>สถานะ</th>
                  <th className="text-center">การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td><strong>{user.username}</strong></td>
                    <td>{user.fullName || '-'}</td>
                    <td>{user.email || '-'}</td>
                    <td>{getUserTypeBadge(user)}</td>
                    <td>
                      {user.isSuperuser ? (
                        <small className="text-muted">ไม่มีข้อจำกัด</small>
                      ) : Array.isArray(user.scopes) && user.scopes.length > 0 ? (
                        <small>{user.scopes.length} สิทธิ์</small>
                      ) : (
                        <small className="text-danger">ไม่มีสิทธิ์</small>
                      )}
                    </td>
                    <td>
                      {user.isActive ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <Button variant="info" size="sm" className="me-1" onClick={() => handleEditClick(user)} title="แก้ไข">
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button variant="warning" size="sm" className="me-1" onClick={() => handlePasswordClick(user)} title="เปลี่ยนรหัสผ่าน">
                        <i className="bi bi-key"></i>
                      </Button>
                      <Button
                        variant={user.isActive ? "secondary" : "success"}
                        size="sm"
                        className="me-1"
                        onClick={() => handleToggleActive(user.id)}
                        title={user.isActive ? "ระงับบัญชี" : "เปิดใช้งานบัญชี"}
                      >
                        <i className={user.isActive ? "bi bi-dash-circle" : "bi bi-check-circle"}></i>
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(user.id)} title="ลบ">
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2"></i>
            {modalMode === 'create' ? 'สร้างผู้ใช้ใหม่' : 'แก้ไขข้อมูลผู้ใช้'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อผู้ใช้ *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              {modalMode === 'create' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>รหัสผ่าน *</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      placeholder="อย่างน้อย 6 ตัวอักษร"
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อ-นามสกุล</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>อีเมล</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="isSuperuser"
                label={<><strong>Superadmin</strong> — มีสิทธิ์เข้าถึงทุกส่วนของระบบ ไม่ต้องกำหนด scopes</>}
                checked={formData.isSuperuser}
                onChange={(e) => setFormData({ ...formData, isSuperuser: e.target.checked })}
              />
            </Form.Group>

            {!formData.isSuperuser && (
              <Card className="border-secondary">
                <Card.Header className="py-2">
                  <strong>
                    <i className="bi bi-shield-check me-2"></i>
                    สิทธิ์การเข้าถึง (Scopes)
                  </strong>
                  <small className="text-muted ms-2">— มีผลเมื่อผู้ใช้เข้าสู่ระบบใหม่</small>
                </Card.Header>
                <Card.Body className="py-2">
                  {SCOPE_GROUPS.map((group) => (
                    <div key={group.label} className={`mb-3 p-2 rounded border border-${group.variant} border-opacity-25 bg-${group.variant} bg-opacity-10`}>
                      <div className={`fw-semibold text-${group.variant} small mb-1`}>
                        {group.label}
                        <span className="text-muted fw-normal ms-2">— {group.description}</span>
                      </div>
                      {group.scopes.map((s) => (
                        <Form.Check
                          key={s.value}
                          type="checkbox"
                          id={`scope-${s.value}`}
                          label={<><code className="me-1">{s.value}</code><small className="text-muted">— {s.label}</small></>}
                          checked={formData.scopes.includes(s.value)}
                          onChange={() => toggleScope(s.value)}
                          className="mb-1"
                        />
                      ))}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            {formData.isSuperuser && (
              <Alert variant="warning" className="mb-0 py-2">
                <i className="bi bi-infinity me-2"></i>
                Superadmin มีสิทธิ์เข้าถึงทุกส่วนของระบบโดยไม่ต้องกำหนด scopes
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)} disabled={processing}>
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" disabled={processing}>
              {processing ? (
                <><Spinner animation="border" size="sm" className="me-2" />กำลังบันทึก...</>
              ) : (
                modalMode === 'create' ? 'สร้างผู้ใช้' : 'บันทึกการเปลี่ยนแปลง'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-key me-2"></i>
            เปลี่ยนรหัสผ่าน - {selectedUser?.username}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>รหัสผ่านใหม่ *</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="อย่างน้อย 6 ตัวอักษร"
              />
              <Form.Text className="text-muted">
                รหัสผ่านจะถูกเปลี่ยนทันที และผู้ใช้จะต้องเข้าสู่ระบบใหม่
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)} disabled={processing}>
              ยกเลิก
            </Button>
            <Button variant="warning" type="submit" disabled={processing}>
              {processing ? (
                <><Spinner animation="border" size="sm" className="me-2" />กำลังเปลี่ยน...</>
              ) : (
                'เปลี่ยนรหัสผ่าน'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default UserManagement;
